// src/admin/BotManagementPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    getAllBots,
    addBot,
    updateBot,
    deleteBot
} from '../api'; // Adjust path as needed

// --- Material UI Imports ---
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import RefreshIcon from '@mui/icons-material/Refresh';
import Link from '@mui/material/Link'; // For Steam profile link
import Visibility from '@mui/icons-material/Visibility'; // <-- Import visibility icons
import VisibilityOff from '@mui/icons-material/VisibilityOff'; // <-- Import visibility icons
import InputAdornment from '@mui/material/InputAdornment'; // <-- Import InputAdornment

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleString();
    } catch (e) {
        console.error("Error formatting date:", e);
        return 'Invalid Date';
    }
};

// --- Add/Edit Modal Component ---
function BotModal({ open, onClose, bot, onSave }) {
    const [originalBotData, setOriginalBotData] = useState(null);
    // --- MODIFICATION: Add sensitive fields to formData state ---
    const [formData, setFormData] = useState({
        steamId: '',
        username: '',
        password: '',
        sharedSecret: '',
        identitySecret: '',
        steamApi: '',
        description: '',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const isEditMode = Boolean(bot);
    // --- NEW: State for password visibility ---
    const [showPassword, setShowPassword] = useState(false);
    const [showSharedSecret, setShowSharedSecret] = useState(false);
    const [showIdentitySecret, setShowIdentitySecret] = useState(false);
    const [showSteamApi, setShowSteamApi] = useState(false);

    useEffect(() => {
        if (bot) {
            // Editing existing bot
            setOriginalBotData(bot);
            setFormData({
                steamId: bot.steamId || '',
                username: bot.username || '',
                // --- MODIFICATION: Populate sensitive fields ---
                password: bot.password || '',
                sharedSecret: bot.sharedSecret || '',
                identitySecret: bot.identitySecret || '',
                steamApi: bot.steamApi || '',
                // --- END MODIFICATION ---
                description: bot.description || '',
            });
            setSaveError('');
        } else {
            // Adding new bot - reset form and original data
            setOriginalBotData(null);
            setFormData({
                steamId: '',
                username: '',
                // --- MODIFICATION: Reset sensitive fields ---
                password: '',
                sharedSecret: '',
                identitySecret: '',
                steamApi: '',
                // --- END MODIFICATION ---
                description: '',
            });
            setSaveError('');
        }
        // Reset visibility on open/change
        setShowPassword(false);
        setShowSharedSecret(false);
        setShowIdentitySecret(false);
        setShowSteamApi(false);
    }, [bot, open]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    // --- NEW: Toggle visibility handlers ---
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleClickShowSharedSecret = () => setShowSharedSecret((show) => !show);
    const handleClickShowIdentitySecret = () => setShowIdentitySecret((show) => !show);
    const handleClickShowSteamApi = () => setShowSteamApi((show) => !show);
    const handleMouseDownPassword = (event) => event.preventDefault(); // Prevent focus loss

    const handleSave = async () => {
        setIsSaving(true);
        setSaveError('');
        try {
            let payload;
            if (isEditMode && originalBotData) {
                // For editing, merge changes from formData into the originalBotData
                payload = {
                    ...originalBotData, // Start with original data
                    // --- MODIFICATION: Include sensitive fields from formData ---
                    username: formData.username,
                    password: formData.password,
                    sharedSecret: formData.sharedSecret,
                    identitySecret: formData.identitySecret,
                    steamApi: formData.steamApi,
                    description: formData.description,
                    // steamId is typically not changed
                };
            } else {
                // For adding, use only formData
                payload = {
                    steamId: formData.steamId,
                    username: formData.username,
                    // --- MODIFICATION: Include sensitive fields from formData ---
                    password: formData.password,
                    sharedSecret: formData.sharedSecret,
                    identitySecret: formData.identitySecret,
                    steamApi: formData.steamApi,
                    description: formData.description,
                };
            }

            // Validate required fields
            if (!payload.steamId) {
                throw new Error("Steam ID is required.");
            }
            if (!/^[0-9]{17}$/.test(payload.steamId)) {
                throw new Error("Invalid Steam ID format. Should be a 17-digit number.");
            }
            // Optional: Add validation for other fields if needed

            await onSave(payload, isEditMode);
            onClose();
        } catch (err) {
            const errMsg = err.response?.data?.title
                || err.response?.data?.message
                || err.message
                || (isEditMode ? 'Failed to update bot.' : 'Failed to add bot.');
            setSaveError(errMsg);
            console.error("Save bot error:", err.response || err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditMode ? 'Edit Bot' : 'Add New Bot'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {saveError && <Alert severity="error">{saveError}</Alert>}
                    <TextField
                        label="Steam ID (64)"
                        name="steamId"
                        value={formData.steamId}
                        onChange={handleChange}
                        required
                        fullWidth
                        variant="outlined"
                        size="small"
                        disabled={isEditMode}
                        helperText={isEditMode ? "Steam ID cannot be changed." : "The 17-digit SteamID64 of the bot account."}
                        inputProps={{ maxLength: 17 }}
                    />
                    <TextField
                        label="Username (Optional)"
                        name="username"
                        value={formData.username || ''}
                        onChange={handleChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        helperText="Optional Steam username associated with the bot."
                    />

                    {/* --- MODIFICATION: Add sensitive fields --- */}
                    <TextField
                        label="Password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password || ''}
                        onChange={handleChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        helperText="Bot account password (handle with extreme care)."
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label="Shared Secret"
                        name="sharedSecret"
                        type={showSharedSecret ? 'text' : 'password'}
                        value={formData.sharedSecret || ''}
                        onChange={handleChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        helperText="Steam Guard shared secret for 2FA codes."
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle shared secret visibility"
                                        onClick={handleClickShowSharedSecret}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                        size="small"
                                    >
                                        {showSharedSecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label="Identity Secret"
                        name="identitySecret"
                        type={showIdentitySecret ? 'text' : 'password'}
                        value={formData.identitySecret || ''}
                        onChange={handleChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        helperText="Steam Guard identity secret for trade confirmations."
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle identity secret visibility"
                                        onClick={handleClickShowIdentitySecret}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                        size="small"
                                    >
                                        {showIdentitySecret ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label="Steam API Key (Optional)"
                        name="steamApi"
                        type={showSteamApi ? 'text' : 'password'}
                        value={formData.steamApi || ''}
                        onChange={handleChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        helperText="Optional Steam Web API key associated with the bot."
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle steam api key visibility"
                                        onClick={handleClickShowSteamApi}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end"
                                        size="small"
                                    >
                                        {showSteamApi ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {/* --- END MODIFICATION --- */}

                    <TextField
                        label="Description"
                        name="description"
                        value={formData.description || ''}
                        onChange={handleChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        multiline
                        rows={2}
                        helperText="Optional description for identifying the bot (e.g., 'Main Trade Bot')."
                    />
                    {isEditMode && bot?.botId && (
                        <TextField
                            label="Bot DB ID"
                            value={bot.botId}
                            disabled
                            fullWidth
                            variant="outlined"
                            size="small"
                            InputProps={{ readOnly: true }}
                        />
                    )}
                    {isEditMode && bot?.addedDate && (
                        <TextField
                            label="Date Added"
                            value={formatDate(bot.addedDate)}
                            disabled
                            fullWidth
                            variant="outlined"
                            size="small"
                            InputProps={{ readOnly: true }}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit" disabled={isSaving}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={isSaving || !formData.steamId}
                    startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSaving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Add Bot')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}


// --- Main Bot Management Page Component ---
// No changes needed in the main component itself, only in the modal it uses.
function BotManagementPage() {
    const [bots, setBots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedBot, setSelectedBot] = useState(null); // For editing

    // Fetch bots function
    const fetchBots = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // IMPORTANT: Ensure your `getAllBots` API call *does* return the sensitive fields
            // if you want them to be pre-filled in the edit modal.
            // This itself is a security risk. Consider if you only want to *set* them, not *view* them.
            const data = await getAllBots();
            const sortedData = [...(data || [])].sort((a, b) => new Date(a.addedDate) - new Date(b.addedDate));
            setBots(sortedData);
        } catch (err) {
            setError('Failed to load bots. Please try refreshing.');
            console.error("Fetch bots error:", err.response?.data || err.message || err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        document.title = 'SpinnerCS - Admin: Bots';
        fetchBots();
    }, [fetchBots]);

    // --- Action Handlers ---
    const handleOpenAddModal = () => {
        setSelectedBot(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (botToEdit) => {
        setSelectedBot(botToEdit);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedBot(null);
    };

    // handleSaveBot receives the full payload (including sensitive fields) from the modal
    const handleSaveBot = async (botPayload, isEditMode) => {
        // Show a loading indicator or disable buttons during save
        // The modal handles its own saving state, but you might want a page-level one too
        // setLoading(true); // Potentially use page-level loading
        // setError(null);
        try {
            if (isEditMode && selectedBot?.botId) {
                await updateBot(selectedBot.botId, botPayload);
            } else {
                await addBot(botPayload);
            }
            fetchBots(); // Refresh the list on success
        } catch (err) {
            // Error is handled within the modal's handleSave,
            // but you could set a page-level error here if the modal fails to show it
            // setError(isEditMode ? 'Failed to update bot.' : 'Failed to add bot.');
            console.error("Save operation failed:", err);
        } finally {
            // setLoading(false); // Stop page-level loading
        }
    };

    const handleDeleteBot = async (botId, botSteamId) => {
        const confirmDelete = window.confirm(`Are you sure you want to remove bot with SteamID "${botSteamId || botId}"? This might affect item assignments if not handled carefully on the backend.`);
        if (confirmDelete) {
            setLoading(true);
            setError(null);
            try {
                await deleteBot(botId);
                setBots(prevBots => prevBots.filter(b => b.botId !== botId));
            } catch (err) {
                setError(`Failed to delete bot ${botId}.`);
                console.error(`Delete bot ${botId} error:`, err.response?.data || err.message || err);
            } finally {
                setLoading(false);
            }
        }
    };

    // --- Render Logic ---
    if (loading && bots.length === 0) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>Bot Account Management</Typography>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Refresh List">
                        <IconButton onClick={fetchBots} disabled={loading} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddModal}
                        disabled={loading}
                    >
                        Add Bot
                    </Button>
                </Stack>
            </Stack>

            {/* --- SECURITY WARNING --- */}
            <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Security Warning:</strong> Bot credentials (password, secrets) are editable here. Ensure access to this page is strictly controlled.
            </Alert>
            {/* --- END SECURITY WARNING --- */}


            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 750 }} aria-label="bot table" size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                            <TableCell>Bot ID</TableCell>
                            <TableCell>Steam ID</TableCell>
                            <TableCell>Username</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Date Added</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bots.map((bot) => (
                            <TableRow
                                key={bot.botId}
                                hover
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell>{bot.botId}</TableCell>
                                <TableCell>
                                    <Link
                                        href={`https://steamcommunity.com/profiles/${bot.steamId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="hover"
                                    >
                                        {bot.steamId}
                                    </Link>
                                </TableCell>
                                <TableCell>{bot.username || '-'}</TableCell>
                                <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    <Tooltip title={bot.description || 'No description'} placement="top-start">
                                        <span>{bot.description || '-'}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>{formatDate(bot.addedDate)}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Edit Bot Details">
                                        <IconButton onClick={() => handleOpenEditModal(bot)} color="primary" size="small" sx={{ mr: 0.5 }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove Bot">
                                        <IconButton
                                            onClick={() => handleDeleteBot(bot.botId, bot.steamId)}
                                            color="error"
                                            size="small"
                                            disabled={loading}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {bots.length === 0 && !loading && (
                <Typography sx={{ mt: 3, textAlign: 'center', color: 'text.secondary' }}>
                    No bots configured. Add one using the button above.
                </Typography>
            )}

            {/* Add/Edit Modal */}
            <BotModal
                open={isModalOpen}
                onClose={handleCloseModal}
                bot={selectedBot}
                onSave={handleSaveBot}
            />
        </Box>
    );
}

export default BotManagementPage;
